import React, { useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';

// Types
import { MachineryManagement, Props } from './types';

// Schema
import { MachineryManagementSchema } from './schema';

// Styles
import { AddMachineryButton, Container, InputsContainer } from './styles';

// Components
import InputText from '../../inputs/InputText';
import DropDownInput from '../../inputs/DropDown/DropDownInput';
import { CheckBox } from '../../inputs/CheckBox';

// Utils
import { useCacheFormInSessionStorage } from '../../../hooks/useCacheForm';
import { Typography } from '../../display/Typography';
import { ImperativeRefInput } from '../../inputs/DropDown/DropDownInput/types';

export const AddMachineryManagementForm = ({
  onFormSubmit,
  namingPersistForm,
  initialData,
  data,
  showInternalCheckbox = false,
  compactMode = false,
  hideButton = false,
  hideTitle = false,
}: Props) => {
  const { t } = useTranslation('forms');
  const {
    handleSubmit,
    control,
    register,
    formState: { errors },
    reset,
  } = useForm<MachineryManagement>({
    defaultValues: {
      ...initialData,
      isInternal:
        initialData.isInternal !== undefined ? initialData.isInternal : true,
    },
    resolver: yupResolver(MachineryManagementSchema) as any,
  });
  useCacheFormInSessionStorage(namingPersistForm, control);

  const [machineryError, setMachineryError] = useState<string | null>(null);

  const auxMachineTypeRef = useRef<ImperativeRefInput>(null);
  const measurementTypeRef = useRef<ImperativeRefInput>(null);

  const onSubmit = async (data: MachineryManagement) => {
    const errorMessage = await onFormSubmit(data);
    if (errorMessage) {
      setMachineryError(errorMessage);
    } else {
      reset({
        name: '',
        type: '',
        typeOfMeasurement: '',
      });
      auxMachineTypeRef.current?.clearSelected();
      measurementTypeRef.current?.clearSelected();
      setMachineryError(null);

      // 🧹 Limpiar cache del formulario después de añadir exitosamente
      // Este es un formulario temporal para añadir NUEVA maquinaria, no tiene sentido persistir
      if (namingPersistForm) {
        console.log(
          '🧹 [AddMachineryManagementForm] Limpiando cache:',
          namingPersistForm
        );
        sessionStorage.removeItem(namingPersistForm);
      }
    }
  };

  // Handle button click
  const handleButtonClick = (e: React.MouseEvent) => {
    e.preventDefault(); // Prevent parent form submission
    handleSubmit(onSubmit)();
  };

  return (
    <Container>
      {!hideTitle && (
        <Typography size="L" variant="semiBold">
          {t('management.addMachinery', 'Añadir maquinaria')}
        </Typography>
      )}

      <InputsContainer>
        <InputText
          {...register('name')}
          label={t('machinery.name', 'Nombre')}
          typeInput={'normal'}
          inputSize="S"
          placeholder={t(
            'machinery.namePlaceholder',
            'Nombre de la maquinaria'
          )}
          borderError={!!errors.name?.message}
        />

        <Controller
          control={control}
          name="type"
          render={({ field: { onChange }, formState: { defaultValues } }) => {
            const options =
              data.auxMachineTypes?.map((type) => ({
                label: type?.name || '',
                value: type?.id || '',
              })) || [];

            return (
              <DropDownInput
                ref={auxMachineTypeRef}
                inputSize="M"
                onChangeSelected={(option) => onChange(option?.value ?? '')}
                typeDropDown="Default"
                labelText={t('machinery.type', 'Tipo de maquinaria')}
                placeholder={t('common:labels.select', 'Seleccionar')}
                defaultValue={defaultValues?.type}
                borderError={!!errors.type?.message}
                options={options}
              />
            );
          }}
        />

        <Controller
          control={control}
          name="typeOfMeasurement"
          render={({ field: { onChange }, formState: { defaultValues } }) => {
            const options =
              data.measureTypes?.map((typeOfMeasurement) => ({
                label: typeOfMeasurement?.name || '',
                value: typeOfMeasurement?.id || '',
              })) || [];

            return (
              <DropDownInput
                ref={measurementTypeRef}
                inputSize="M"
                onChangeSelected={(option) => onChange(option?.value ?? '')}
                typeDropDown="Default"
                labelText={t('machinery.measurementType', 'Tipo de medida')}
                placeholder={t('common:labels.select', 'Seleccionar')}
                defaultValue={defaultValues?.typeOfMeasurement}
                borderError={!!errors.typeOfMeasurement?.message}
                options={options}
              />
            );
          }}
        />

        {showInternalCheckbox && (
          <Controller
            control={control}
            name="isInternal"
            render={({ field: { onChange, value } }) => (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.8rem',
                }}
              >
                <CheckBox
                  checked={value !== undefined ? value : true}
                  onChange={(e) => onChange(e.target.checked)}
                  indeterminate={false}
                />
                <Typography size="S">
                  {t('workEquipment.internal', 'Interno')}
                </Typography>
              </div>
            )}
          />
        )}

        {!hideButton && (
          <AddMachineryButton type="button" onClick={handleButtonClick}>
            ⮕
          </AddMachineryButton>
        )}
        {machineryError && (
          <Typography colorText="lightCoralRed" size="XS">
            {machineryError}
          </Typography>
        )}
      </InputsContainer>
    </Container>
  );
};
